import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';

export function AuthScreen({ onAuth }: { onAuth: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login'
        ? { email, password }
        : { email, username, password };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Authentication failed');
        return;
      }
      setAuth(data.token, data.playerId, data.username);
      onAuth();
    } catch {
      setError('Connection failed — is the server running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoText}>ANNOPOLIS</span>
          <span style={styles.logoSub}>Seed the cosmos. Shape existence.</span>
        </div>

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }} onClick={() => setMode('login')}>Login</button>
          <button style={{ ...styles.tab, ...(mode === 'register' ? styles.tabActive : {}) }} onClick={() => setMode('register')}>Register</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'register' && (
            <input
              style={styles.input}
              placeholder="Username (3-20 chars, letters/numbers/_)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          )}
          <input
            style={styles.input}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Connecting...' : mode === 'login' ? 'Deploy System' : 'Create System'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  screen: {
    width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(ellipse at 50% 50%, #0d1520 0%, #0a0e1a 70%)',
  },
  card: {
    width: 360,
    background: 'rgba(13,21,32,0.95)',
    border: '1px solid rgba(74,240,196,0.2)',
    borderRadius: 8,
    padding: 32,
    display: 'flex', flexDirection: 'column', gap: 20,
    boxShadow: '0 0 40px rgba(74,240,196,0.08)',
  },
  logo: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  logoText: { fontSize: 28, fontWeight: 800, letterSpacing: '0.15em', color: '#4af0c4' },
  logoSub: { fontSize: 11, color: '#4a6080', letterSpacing: '0.05em', fontStyle: 'italic' },
  tabs: { display: 'flex', gap: 4, background: 'rgba(74,240,196,0.05)', borderRadius: 6, padding: 3 },
  tab: {
    flex: 1, padding: '6px 0', background: 'none', border: 'none',
    color: '#4a6080', cursor: 'pointer', borderRadius: 4, fontSize: 13, fontWeight: 500,
  },
  tabActive: { background: 'rgba(74,240,196,0.12)', color: '#4af0c4', fontWeight: 700 },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  input: {
    padding: '10px 12px',
    background: 'rgba(74,240,196,0.04)',
    border: '1px solid rgba(74,240,196,0.15)',
    borderRadius: 6,
    color: '#e0e8ff', fontSize: 13, outline: 'none',
  },
  error: { color: '#f87171', fontSize: 12, padding: '4px 0' },
  submitBtn: {
    padding: '12px',
    background: 'rgba(74,240,196,0.15)',
    border: '1px solid rgba(74,240,196,0.4)',
    borderRadius: 6,
    color: '#4af0c4', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', letterSpacing: '0.08em',
    transition: 'all 0.15s',
  },
};
